import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { GraphEmptyState } from "renderers/multi-line-graph/components/graph-states"
import { numberFormatter } from "helpers/formatters/numbers"
import { createBetaMultiDateQuery, createBetaIntradayBatchTrendQueries } from "gatherers/multi-date/beta"
import { PREDICTED_BETA, HISTORICAL_BETA, CUSTOM_BETA } from "gatherers/constants"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { filterItemsWithData, extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { betasAreMissing } from "selectors/missingDataFiles"
import { usePositionSetBetaInstrument } from "connected-components/asset-view/helpers"
import type { ModelMetadata } from "resources/models"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useGraphData } from "../../hooks/use-graph-data"
import { useSourceGraphMetricIds, getEnabledItemIds } from "../../hooks/use-source-graph-metric-ids"

interface BetaItem {
  id: string
  label: string
  disabled: boolean
  color?: string
}

const NO_BETA_DATA_MESSAGE = "This risk model has no beta data."

const DAY_BASED_SERIES = [
  { id: PREDICTED_BETA, label: "Predicted Beta", missingDataFile: "predictedBeta" },
  { id: HISTORICAL_BETA, label: "Historical Beta", missingDataFile: "historicalBeta" },
] as const

interface BetaGraphProps {
  modelMetadata: ModelMetadata
  instrumentsWithMetadata: { original?: Record<string, unknown> }
  onMetricsChange?: SourceMetricIdsChangeHandler
}

export default function BetaGraph({ modelMetadata, instrumentsWithMetadata, onMetricsChange }: BetaGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
  } = useGraphData()

  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id
  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const missingDataFiles = modelMetadata.missingDataFiles
  const resolvedBetaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata?.original, modelMetadata)
  // Dynamic beta aliases the model's predicted beta, so it has nothing to return without it.
  const betaInstrument = missingDataFiles?.includes("predictedBeta") ? null : resolvedBetaInstrument

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined, legendClickedDate?: string, legendClickedBatchId?: string) => {
    if (!panelId) return
    // Beta has no per-series drilldown target yet; kept for parity with peer templates' onDotClick signature.
    void focusedItemId
    // A legend click has no clickPosition, so legendClickedBatchId stands in for clickPosition.batchId.
    const clickedBatch = isBatchTrendMode
      ? completedBatches.find(batch => batch.id === (clickPosition?.batchId ?? legendClickedBatchId))
      : undefined
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? legendClickedDate ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.beta",
      title: "Asset View: Beta",
      view: "beta",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A day-based point has no clickedBatch; falling back to the latest batch would misdate it.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: { clickedDate: snapshotDate },
    }))
  }, [panelId, openDrilldown, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  // Same intent as a chart-dot click, seeded with the series' own last date — the legend has no click coordinates.
  const handleLegendValueClick = useCallback((focusedItemId: string, legendClickedDate?: string, legendClickedBatchId?: string) => {
    handleDotClick(undefined, focusedItemId, legendClickedDate, legendClickedBatchId)
  }, [handleDotClick])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter ?? undefined, normalization)
  const segment = useFilteredSegment(portfolioFilter ?? undefined, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors()
  const currentItems = useMemo(() => {
    const available: { id: string, label: string }[] = DAY_BASED_SERIES
      .filter(series => !missingDataFiles?.includes(series.missingDataFile))
      .map(({ id, label }) => ({ id, label }))
    if (betaInstrument) available.push({ id: CUSTOM_BETA, label: "Dynamic Beta" })

    return available.map((series, index) => ({
      ...series,
      color: chartColors.series[index % chartColors.series.length],
      disabled: Boolean(disabledItems[series.id]),
    }))
  }, [missingDataFiles, betaInstrument, chartColors.series, disabledItems])

  const enabledBetaSeriesIds = useMemo(
    () => getEnabledItemIds(currentItems),
    [currentItems],
  )
  useSourceGraphMetricIds({ itemIds: enabledBetaSeriesIds, onMetricsChange })

  const filterItems = useCallback(
    (items: BetaItem[], data: Record<string, unknown>[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => {
    const dayBasedQuery = createBetaMultiDateQuery(modelMetadata.id, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, {
      batchId: intradayBatchId, lastIntradayBatchId, betaInstrument, missingDataFiles,
    })
    if (!isBatchTrendMode) return [dayBasedQuery]
    return [dayBasedQuery, ...createBetaIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches, { missingDataFiles })]
  }, [modelMetadata.id, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId, betaInstrument, missingDataFiles, isBatchTrendMode, completedBatches])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const noDataMessage = intradayChartMode !== "none" ? "Intraday beta data isn't available yet." : undefined

  if (betasAreMissing(missingDataFiles)) {
    return <GraphEmptyState message={NO_BETA_DATA_MESSAGE} />
  }

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      onLegendValueClick: handleLegendValueClick,
      // Beta is a ratio; the renderer otherwise defaults to a percentage formatter.
      formatter: numberFormatter,
      loadingMessage: "Loading beta data...",
      errorMessage: "Failed to load beta data. Please try again.",
      emptyMessage: "No beta data available.",
      noDataMessage,
      isOneDayMode: intradayChartMode !== "none",
      intradayChartMode,
      isTruncated,
      // Right-hand legend panel but no "+ Add series" control: Beta has no resolvable metric family, same as its "Duplicate as view" exclusion.
      legendPlacement: "right",
    }}
  />
}
